import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import { useDispatch } from "react-redux";
import ScrollToTop from "./components/ScrollToTop";
import AuthCallback from "./influencer/auth/AuthCallback";
import Influencer from "./influencer/pages/Influencer";
import InfluencerProtectedRoute from "./influencer/routes/InfluencerProtectedRoute";
import OurWork from "./components/OurWork";
import HomePage from "./components/HomePage";
import ContactSupport from "./components/ContactDetails";
import AboutInfuber from "./components/AboutInfuber";
import PublicCampaign from "./influencer/publicCampaign/Campaigns";
import CampaignDetails from "./influencer/publicCampaign/campaignDetails";
import CampaignSubmissionForm from "./influencer/publicCampaign/CampaignSubmissionForm";
import Brands from "./components/Brands";
import OurStory from "./components/OurStory";
import CareerPage from "./components/CareerPage";
import PrivacyPolicy from "./components/PrivacyPolicy";
import { initGA, trackPageView } from "./utils/analytics";
import AffiliateRedirect from "./influencer/affiliateLink/AffiliateRedirect";
import Tracker from "./tracker/Tracker";
import ThankYou from "./influencer/automations/payments/ThankYou";
import CompanyRoutes from "./company/routes/CompanyRoutes";
import InfluencerRoutes from "./influencer/routes/InfluencerRoutes";
import AdminRoutes from "./admin/routes/AdminRoutes";
import { verifySession } from "./admin/services/adminService";
import { setAdmin } from "./Redux/adminSlice";
import PublicProfile from "./influencer/pages/PublicProfile";
import { fetchInitialProfile } from "./Redux/userSlice";
import { fetchInitialCompanyProfile } from "./Redux/companySlice";
import JobDetailsPage from "./components/JobDetailsPage";
// import NotFoundPage from "./components/NotFoundPage";
import MarketPlace from "./marketplace/pages/MarketPlace";
import ProtectedInfluencerDetailsPage from "./marketplace/pages/ProtectedInfluencerDetailsPage";
import PricingPage from "./modules/pricing/PricingPage";
import InstaToolPricingPage from "./modules/insta-tools/InstaToolPricingPage";
import InstaToolInfo from "./components/instaToolInfo";
import { CheckoutProvider } from "./modules/checkout/CheckoutContext";
import CheckoutModal from "./modules/checkout/components/CheckoutModal";
import { InfluencerCheckoutProvider } from "./modules/influencer-subscription/context/InfluencerCheckoutContext";
import InfluencerCheckoutModal from "./modules/influencer-subscription/components/InfluencerCheckoutModal";

/** Redirects /reset-password/:token to influencer reset-password (backward compat) */
const RedirectToInfluencerResetPassword = () => {
  const { token } = useParams();
  return <Navigate to={`/influencer/reset-password/${token}`} replace />;
};

/** Redirects /login to /influencer/login while preserving query params (e.g. redirectTo for post-login redirect) */
const RedirectLoginToInfluencer = () => {
  const location = useLocation();
  const search = location.search || "";
  return <Navigate to={`/influencer/login${search}`} replace />;
};

// Google Analytics page tracker
const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const trackablePages = ["/", "/login", "/brands"];
    if (trackablePages.includes(location.pathname)) {
      trackPageView(location.pathname);
    }
  }, [location]);

  return null;
};

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    initGA(); // Initialize Google Analytics once
  }, []);

  useEffect(() => {
    const userType = localStorage.getItem("userType");

    if (userType === "admin") {
      const checkAdminSession = async () => {
        try {
          const response = await verifySession();
          if (response.success) {
            dispatch(setAdmin(response.admin));
          }
        } catch (error) {
          localStorage.removeItem("userType");
        }
      };
      checkAdminSession();
    } else if (userType === "influancer") {
      dispatch(fetchInitialProfile());
    } else if (userType === "company") {
      dispatch(fetchInitialCompanyProfile());
    } else {
      localStorage.removeItem("userType"); // fallback for invalid state
    }
  }, [dispatch]);

  return (
    <CheckoutProvider>
      <InfluencerCheckoutProvider>
      <Router>
        <PageTracker />
        <ScrollToTop />
        <CheckoutModal />
        <InfluencerCheckoutModal />
        <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/our-work" element={<OurWork />} />
        <Route path="/about-infuber" element={<AboutInfuber />} />
        <Route path="/brands" element={<Brands />} />
        <Route path="/contact-details" element={<ContactSupport />} />
        <Route path="/our-story" element={<OurStory />} />
        <Route path="/privacy policy" element={<PrivacyPolicy />} />
        <Route path="/career-page" element={<CareerPage />} />
        <Route path="/career/:jobId" element={<JobDetailsPage />} />
        <Route path="/influencer" element={<Influencer />} />
        <Route path="/campaign" element={<PublicCampaign />} />
        <Route path="/campaign/:id" element={<CampaignDetails />} />
        <Route
          path="/campaign/:id/submit"
          element={
            <InfluencerProtectedRoute>
              <CampaignSubmissionForm />
            </InfluencerProtectedRoute>
          }
        />
        <Route path="/u/:username" element={<PublicProfile />} />
        <Route path="/marketplace" element={<MarketPlace />} />
        <Route path="/marketplace/influencer/:username" element={<ProtectedInfluencerDetailsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/insta-tools/pricing" element={<InstaToolPricingPage />} />
        <Route path="/insta-tools/info" element={<InstaToolInfo />} />

        {/* Backward-compat: redirect root auth to influencer routes */}
        <Route path="/login" element={<RedirectLoginToInfluencer />} />
        <Route path="/forgot-password" element={<Navigate to="/influencer/forgot-password" replace />} />
        <Route path="/reset-password/:token" element={<RedirectToInfluencerResetPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Influencer Routes (dashboard, auth, profile – same structure as Company) */}
        <Route path="/influencer/*" element={<InfluencerRoutes />} />

        {/* Payment & Redirect Pages */}
        <Route path="/tracker" element={<Tracker />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/a/:shortCode" element={<AffiliateRedirect />} />

        {/* Company & Admin Routes */}
        <Route path="/company/*" element={<CompanyRoutes />} />
        <Route path="/auth/admin/*" element={<AdminRoutes />} />

        {/* Future route for affiliate marketplace */}
        {/* <Route path="/affiliate-marketplace" element={<AffiliateMarketplace />} /> */}
        
        {/* Catch-all route for 404 errors */}
        {/* <Route path="*" element={<NotFoundPage />} /> */}
        </Routes>
      </Router>
      </InfluencerCheckoutProvider>
    </CheckoutProvider>
  );
}

export default App;
